import { Capacitor } from "@capacitor/core";

interface BlaxWebSocket {
  socket_id?: string;
  readyState?: 0|1|2|3;
  send: (data: any) => void;
  addEventListener: (event: string, callback: (data: any) => void) => any;
  removeEventListener: (event: string, callback: (data: any) => void) => any;
}

interface BlaxWebSocketClass{
  socket: BlaxWebSocket | null;
  channels: any[];
  is_opened: Ref<boolean>;
  is_setup: Ref<boolean>;
  is_connecting_socket: Ref<boolean>;
  is_after_lost_connection: Ref<boolean>;
  heartbeat: any;
  last_reconnect_try: number;
  send_queue: any[];
  connect: (force_reset?: boolean) => Promise<BlaxWebSocket>;
  channel: (channel_name: string) => Promise<WebsocketChannel|undefined>;
  send<T>(event: string, data?: object, channel_name?: string|null, progress?: (data: any) => void): Promise<any|T>;
}


class WebsocketChannel extends EventTarget{
  name: string
  is_establishing: boolean = false
  is_established: boolean = false
  has_wsauth: boolean = false
  authresponse: any = null

  constructor(
    channel_name: string
  ) {
    console.log('[wc] %cCreating new channel ' + channel_name ,'color:#aaFFff')
    super()
    this.name = channel_name
    ws.channels.push(this)
  }

  async establish() {
    if(this.is_established && ws.is_setup.value){
      return new Promise((resolve, _) => {
        resolve(this)
      })
    }

    if (this.is_establishing){
      console.log('[wc] Awaiting Establishing', this.name)
      await this.awaitEstablishing()
    }

    console.log('[wc] %cEstablishing '+ this.name,'color:#ff00ff')
    this.is_establishing = true

    // await ws.is_opened.value to avoid race condition
    await (new Promise((resolve, _) => {
      const inter = setInterval(() => {
        if(ws.is_opened.value){
          console.log('[wc] %cAwaited socket opened','color:lightgreen')
          clearInterval(inter)
          resolve(true)
        }else{
          console.log('[wc] %cAwaiting socket open','color:lightgreen')

          if (Date.now() - ws.last_reconnect_try > 4000){
            ws.connect(true)
          }
        }
      }
      , 10)
    }))

    await (new Promise((resolve, _) => {
      const inter = setInterval(() => {
        if(ws.socket?.socket_id){
          console.log('[wc] %cAwaited socket id','color:lightgreen')
          clearInterval(inter)
          resolve(true)
        }else{
          console.log('[wc] %cAwaiting socket id','color:lightgreen')
        }
      }, 100)
    }))

    const res = (this.authresponse) ? this.authresponse : await new Promise((resolve, reject) => {
      console.log('Broadcasting auth')
      api.post('broadcasting/auth',{
        socket_id: ws?.socket?.socket_id,
        channel_name: this?.name
      },{
        'Content-Type': 'application/x-www-form-urlencoded',
      }).then((r) =>  {
        this.authresponse = r
        resolve(r)
        return r;
      }).catch((error) => {
        this.is_establishing = false
        this.is_established = false

        reject(error)
      })
    })

    console.log(
      'Broadcasting auth response',
      ws.is_opened.value,
      ws.is_setup.value,
    )
    return await this.send('pusher.subscribe', {
      channel: this.name,
      auth: res?.data?.auth,
      authtoken: user?.value?.token ?? localStorage.getItem('bearerToken')
    }).then((_) => {
      console.log('[ws] %cChannel established' + this.name,'color:pink')
      this.is_established = true
      ws.is_setup.value = true
      return this
    })
  }

  async awaitEstablishing() {
    return new Promise((resolve, _) => {
      const inter = setInterval(() => {
        if(this.is_established){
          clearInterval(inter)
          resolve(this)
        }
      }, 100)
    })
  }

  async send(event: string, data: any) {
    if(!this.is_establishing && !this.is_established && event != 'pusher.subscribe'){
      console.log('[wc] Awaiting Establishing', this.name)
      await this.establish()
    }

    if (this.is_establishing && !this.is_established && event != 'pusher.subscribe') {
      return new Promise((resolve, _) => {
        const inter = setInterval(() => {
          if(this.is_established){
            clearInterval(inter)
            resolve(ws.send(
              event,
              data,
              null
            ))
          }
        }, 100)
      })
    }

    return ws.send(
      event,
      data,
      this.name
    )
  }
}

export const ws = new class extends EventTarget{
  socket: BlaxWebSocket | null = null
  channels: any[] = []
  is_opened = ref<boolean>(false)
  is_setup = ref<boolean>(false)
  is_connecting_socket = ref<boolean>(false)
  is_after_lost_connection = ref<boolean>(false)
  heartbeat: any
  last_reconnect_try: number = 0

  send_queue: any[] = []

  constructor() {
    super()
    console.log('[ws] Constructed')

    // watch(() => [
    //   this.is_opened.value,
    //   this.is_setup.value,
    //   this.is_connecting_socket.value,
    //   this.is_after_lost_connection.value
    // ], (newVal) => {
    //   console.log('[ws] %cState changed', 'color:#ff00ff', newVal)
    // })
  }

  async connect(force_reset: boolean = false) {
    if(
      this.socket?.readyState != 3
      && this.socket?.readyState !== null
      && this.socket?.readyState !== undefined
    ){
      // console.log('[ws] %cCancel socket due to state is ' +this.socket?.readyState ,'color:#ff0000')
      return
    }

    if (this.last_reconnect_try && (Date.now() - this.last_reconnect_try) < 3000) {
      return console.log('[ws] %cReconnect too fast, waiting', 'color:#ff0000');
    }

    this.last_reconnect_try = Date.now()

    console.log('[ws] %cWS Connecting', 'color:#ff0000')

    // === Init heartbeat
    if(force_reset || !this.heartbeat){
      if (this.heartbeat){
        clearInterval(this.heartbeat)
      }
      this.heartbeat = setInterval(() => {
        this.send('pusher.ping')
      }, 20000)
    }

    // === Reset channels
    if(force_reset) {
      this.channels = []
    }

    if(this.channels.length == 0){
      console.log('[WS] No channels, adding private-websocket')
      this.channel('private-websocket')
    }

    // === Reset socket
    if(force_reset){
      this.socket = null
    }

    return new Promise((resolve, reject) => {
      if(this.socket){
        if(this.socket?.readyState === 1){
          return resolve(this.socket);
        }
        return false;
      }

      if(this.is_connecting_socket.value){
        return console.log('[ws] %cSocket already connecting','color:#ff0000')
      }

      this.is_connecting_socket.value = true

      console.log('[ws] %cOpening socket','color:#33ff33')
      this.socket = new WebSocket('wss://'+useRuntimeConfig().public.WEBS_URL) as BlaxWebSocket

      this.socket.addEventListener('error', (error) => {
        console.log('Error', error)
        this.is_connecting_socket.value = false
        // clearInterval(this.heartbeat)
        reject(error)
      })

      this.socket.addEventListener('close', (error) => {
        console.log('Close at '+(Math.round(window.performance.now() / 10)/100), error)
        // clearInterval(this.heartbeat)

        // remove all channels
        this.channels = []
        this.is_connecting_socket.value = false
        this.is_opened.value = false
        this.is_setup.value = false
        this.socket = null

        this.is_after_lost_connection.value = true

        if(!Capacitor.isNativePlatform()){
          notify({
            id: 'websocket-connection-state',
            type: 'info',
            text: 'The websocket connection is trying to reconnect.<br><br>This happens due to updates or inactivity on your side.<br><br>If this message disappears, the connection is restored.',
            timeout: 50000,
          })
        }

        reject(error)
      })

      this.socket.addEventListener('open', () => {
        if(this.is_after_lost_connection.value && !Capacitor.isNativePlatform()){
          notify({
            id: 'websocket-connection-state',
            type: 'success',
            text: 'Websocket connection restored',
            timeout: 1000,
          })
        }

        console.log('[ws] %cOpened', 'color:pink')
        this.is_opened.value = true

        setTimeout(() => {
          this.workSendQueue()
        },3000);
      })

      const list = this.socket.addEventListener('message', async (message) => {
        message = JSON.parse(message.data)

        if(message?.data && message?.event == "pusher.connection_established"){
          const data = JSON.parse(message.data)

          if(data?.socket_id && this.socket){
            this.socket.socket_id = data.socket_id

            resolve(this.socket)
            this.socket.removeEventListener('message', list)
          }
        }else{
          // fire event within class
          this.dispatchEvent(new CustomEvent(message.event,{
            detail: {
              event: message.event,
              data: message.data,
              channel: message.channel
            }
          }))

          devAppendLog(message, 'websocket-receive')
        }
      })
    })
  }

  async channel(channel_name: string) : Promise<WebsocketChannel|undefined> {
    // Check if channel is already existing
    let channel = this.channels.find((c) => {
      if(c.name == channel_name) return c
    })

    // Use existing one
    if(!channel){
      channel = new WebsocketChannel(channel_name)
    }

    return channel.establish()
  }

  async workSendQueue() {
    if(this.send_queue.length > 0){
      console.log('[ws] Queue has items', this.send_queue.length)
      const queue = this.send_queue
      this.send_queue = []

      queue.forEach(async (payload) => {
        console.log('[ws] Queue real send %c' + payload.event, 'color:lightgreen')
        await this.channel(payload.channel)
        this.socket?.send(JSON.stringify(payload))
      })
    }else{
      console.log('[ws] Queue is empty')
    }
  }

  async send<T>(
    event: string,
    data: object = {},
    channel_name: string|null = 'private-websocket',
    progress?: (data: any) => void
  ) : Promise<any|T> {

    if(!channel_name){
      channel_name = 'private-websocket'
    }

    if(!this.socket){
      console.log('[ws] %cSocket not opened','color:#ff0000')
      await this.connect()
    }

    const unique = '['+Math.random().toString(36).substring(7)+']'
    let sendingevent = event + unique;

    if (event == 'pusher.subscribe') {
      sendingevent = 'pusher.subscribe'
      channel_name = null
    }

    const send_start = window.performance.now()
    const payload = {
      event: sendingevent,
      data,
      channel: channel_name,
    }

    if(
      channel_name
      && !(
        event == 'pusher:subscribe'
        || event == 'pusher.subscribe'
      )
    ){
      await this.channel(channel_name as string)
    }

    if(this.is_opened.value && this.socket?.readyState === 1){
      this.socket.send(JSON.stringify(payload))
    }else{
      this.send_queue.push(payload)
    }

    devAppendLog(payload, 'websocket-send')

    await this.connect()
    if (!this.socket) throw new Error('Socket not connected')

    return new Promise((resolve, reject) => {
      const handler = (message: any) => {
        message = message.detail

        // Listen for Success
        if(
          (
            event == 'pusher.subscribe'
            // && message?.event.includes('channel:joined:response')
            && message?.data?.channel == channel_name
          )
          || message?.event.includes(sendingevent+':response')
        ) {
          this.removeEventListener(sendingevent+':progress', handler)
          this.removeEventListener(sendingevent+':error', handler)
          this.removeEventListener(sendingevent+':response', handler)

          console.log('%c[diff.'+sendingevent+'] at '+(Math.round(window.performance.now() / 10)/100),'color:red', window.performance.now() - send_start)
          resolve(message.data)
        }

        // Listen for Errors
        if(
          message?.event.includes(sendingevent+':error')
          || message?.event.includes(sendingevent+':timeout')
        ){
          this.removeEventListener(sendingevent+':progress', handler)
          this.removeEventListener(sendingevent+':error', handler)
          this.removeEventListener(sendingevent+':response', handler)

          console.log('%c[diff.'+sendingevent+'] at '+(Math.round(window.performance.now() / 10)/100),'color:red', window.performance.now() - send_start)
          reject(message.data)
        }

        // Listen for Progress
        if (
          progress
          && message?.event.includes(sendingevent+':progress')
        ) {
          progress(message.data)
        }
      }

      this.addEventListener(sendingevent+':progress', handler);
      this.addEventListener(sendingevent+':error', handler);
      this.addEventListener(sendingevent+':response', handler);
    })
  }
} as BlaxWebSocketClass
